// components/TabbedDetails/index.tsx
import React, { useState, ReactNode } from 'react';
import {
  FaArrowLeft, FaShareAlt, FaFlag, FaTimes,
  FaEnvelope, FaClock
} from 'react-icons/fa';
import { MdLocationOn } from 'react-icons/md';
import { LoaderCircle, Sparkles, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TabbedDetailsProps } from './types';
import { getRenderer } from './renderers';
import { formatDate, getCompatibilityColor } from './utils';

export function MyTabbedDetails<T = any>({
  data,
  isLoading = false,
  error = null,
  tabs,
  sections,
  contents,
  actionButtons = [],
  title,
  headColor = 'bg-gradient-to-r from-indigo-600 to-indigo-800',
  coverImage,
  profileImage,
  subtitle,
  location,
  joinDate,
  profileViews = 0,
  completionPercentage = 0,
  status = '',
  isActive = false,
  compatibilityScore,
  summary,
  getValue: externalGetValue,
  onBack,
  onShare,
  onReport,
  onAction,
  onSendRequest,
  isActionLoading = false,
  isRequestLoading = false,
  renderHeader,
  renderStats,
  renderFooter,
  className = '',
  contentClassName = '',
  showBackButton = true,
  showShareButton = true,
  showReportButton = true,
  showStats = true,
  showFooter = true,
  backLabel = 'Back',
  shareLabel = 'Share',
  reportLabel = 'Report',
  viewsLabel = 'Views',
  completeLabel = 'Complete',
  statusLabel = 'Status',
  lastActiveLabel = 'Last active',
  sendRequestLabel = 'Send Request',
  reportReasons = ['Inappropriate Content', 'Fake Profile', 'Harassment', 'Spam', 'Other'],
  onReportSubmit,
}: TabbedDetailsProps<T>) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || '');
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');

  // Default value getter
  const getValue = (key: string, fromLookingFor = false) => {
    if (externalGetValue) return externalGetValue(key, fromLookingFor);
    if (!data) return null;

    if (fromLookingFor && (data as any).looking_for) {
      return (data as any).looking_for[key];
    }
    return (data as any)[key];
  };

  // Get reaction state for action buttons
  const getReactionState = (actionId: string): boolean => {
    if (!data) return false;
    const myReactionType = (data as any).my_reaction_type;
    const iLiked = (data as any).i_liked;

    if (actionId === 'like') return iLiked || myReactionType === 'like';
    if (actionId === 'save') return myReactionType === 'save';
    if (actionId === 'super_like') return myReactionType === 'super_like';
    return false;
  };

  // Get sections for current tab
  const getSectionsForTab = () => {
    const sectionIds = new Set<string>();
    contents.filter(content => content.tab === activeTab).forEach(content => {
      sectionIds.add(content.section);
    });
    return sections.filter(section => sectionIds.has(section.id));
  };

  // Get content for a specific section
  const getContentForSection = (sectionId: string) => {
    return contents.filter(content => content.tab === activeTab && content.section === sectionId);
  };

  // Unified renderer using registry pattern
  const renderContentByType = (content: any) => {
    const value = getValue(content.key, content.lookingFor || false);
    const renderer = getRenderer(content.type);
    
    if (!renderer) {
      console.warn(`No renderer found for content type: ${content.type}`);
      return null;
    }
    
    return renderer({ content, value, data, getValue });
  };

  // Handle share
  const handleShare = async () => {
    if (onShare) {
      onShare();
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch {
        alert('Failed to copy link');
      }
    }
    setShowMenu(false);
  };

  // Handle report submit
  const handleReportSubmit = () => {
    if (!reportReason) {
      alert('Please select a reason');
      return;
    }
    if (onReportSubmit) {
      onReportSubmit(reportReason, reportDescription);
    } else if (onReport) {
      onReport();
    }
    setShowReportModal(false);
    setReportReason('');
    setReportDescription('');
    setShowMenu(false);
  };

  // Handle action
  const handleAction = (actionId: string, isAdding: boolean) => {
    if (onAction) {
      onAction(actionId, isAdding);
    }
  };

  // Handle send request
  const handleSendRequest = () => {
    if (onSendRequest) {
      onSendRequest();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoaderCircle className="animate-spin text-green-600 mx-auto mb-4" size={48} />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-6xl text-red-500 mx-auto mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Not Found</h2>
          <p className="text-gray-600 mb-6">{error?.message || 'Data not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 pb-24 ${className}`}>
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="flex items-center justify-between px-4 py-3">
          {showBackButton && (
            <button
              onClick={onBack || (() => navigate(-1))}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition"
            >
              <FaArrowLeft className="text-gray-700" size={18} />
            </button>
          )}

          <div className="flex items-center gap-2">
            {compatibilityScore && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${getCompatibilityColor(compatibilityScore)}`}>
                <Sparkles size={14} />
                <span className="text-sm font-semibold">{compatibilityScore}% Match</span>
              </div>
            )}

            {(showShareButton || showReportButton) && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition"
                >
                  <MoreVertical size={18} className="text-gray-700" />
                </button>

                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-lg py-2 z-20">
                      {showShareButton && (
                        <button
                          onClick={handleShare}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <FaShareAlt size={14} />
                          {shareLabel}
                        </button>
                      )}
                      {showReportButton && (
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            setShowReportModal(true);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <FaFlag size={14} />
                          {reportLabel}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Header Renderer */}
      {renderHeader ? (
        renderHeader(data)
      ) : (
        <div className="relative max-w-2xl mx-auto mt-4 bg-white rounded-xl shadow-md overflow-hidden">
          {/* Cover Photo */}
          <div className={`h-40 w-full ${headColor}`}>
            {coverImage && <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-black/20"></div>
          </div>

          {/* Profile Photo */}
          <div className="absolute top-10 left-4">
            <div className="relative">
              <img
                className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg"
                src={profileImage || 'https://via.placeholder.com/96'}
                alt={title || 'Profile'}
              />
              {isActive && (
                <div className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>
          </div>

          {/* Name and Basic Info */}
          <div className="pt-5 px-4 pb-4 bg-white">
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-800">{title || 'User'}</h1>
              {subtitle && <p className="text-gray-500 text-sm">{subtitle}</p>}
              {(location || joinDate) && (
                <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
                  {location && (
                    <span className="flex items-center gap-1">
                      <MdLocationOn size={14} className="text-green-500" />
                      {location}
                    </span>
                  )}
                  {location && joinDate && <span className="w-1 h-1 bg-gray-300 rounded-full"></span>}
                  {joinDate && (
                    <span className="flex items-center gap-1">
                      <FaClock size={12} className="text-green-500" />
                      Joined {formatDate(joinDate)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Summary */}
            {summary && (
              <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl">
                <p className="text-gray-700 text-sm italic">💭 "{summary}"</p>
              </div>
            )}
          </div>

          {/* Stats Row */}
          {showStats && (renderStats ? renderStats(data) : (
            <div className="bg-white px-4 py-3 border-t border-gray-100 flex justify-around">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800">{profileViews}</div>
                <div className="text-xs text-gray-500">{viewsLabel}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800">{completionPercentage}%</div>
                <div className="text-xs text-gray-500">{completeLabel}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800 capitalize">{status || 'Active'}</div>
                <div className="text-xs text-gray-500">{statusLabel}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      {actionButtons.length > 0 && (
        <div className="bg-white px-4 py-4 border-b border-gray-100">
          <div className="flex gap-3">
            {actionButtons.map((btn) => {
              const isActiveReaction = getReactionState(btn.id);
              const Icon = isActiveReaction ? btn.activeIcon : btn.icon;
              return (
                <button
                  key={btn.id}
                  onClick={() => handleAction(btn.id, !isActiveReaction)}
                  disabled={isActionLoading}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                    isActiveReaction
                      ? `${btn.bgColor} text-white shadow-md`
                      : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                  } ${isActionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isActionLoading ? (
                    <LoaderCircle className="animate-spin" size={18} />
                  ) : (
                    <Icon size={18} />
                  )}
                  <span className="text-sm">{btn.labelKey}</span>
                </button>
              );
            })}
          </div>

          {onSendRequest && (
            <button
              onClick={handleSendRequest}
              disabled={isRequestLoading}
              className={`w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all ${
                isRequestLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md active:shadow-lg'
              }`}
            >
              {isRequestLoading ? (
                <LoaderCircle className="animate-spin" size={18} />
              ) : (
                <FaEnvelope size={16} />
              )}
              {sendRequestLabel}
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-[57px] z-10">
        <div className="flex overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActiveTab = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all relative whitespace-nowrap ${
                  isActiveTab ? `text-${tab.color || 'blue'}-600` : 'text-gray-500'
                }`}
              >
                {Icon && <Icon size={16} />}
                <span>{tab.label}</span>
                {isActiveTab && (
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-${tab.color || 'blue'}-500 rounded-full`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic Content Sections with Registry Pattern */}
      <div className={`p-4 space-y-5 ${contentClassName}`}>
        {getSectionsForTab().map((section) => {
          const sectionContents = getContentForSection(section.id);
          if (sectionContents.length === 0) return null;

          const Icon = section.icon;

          return (
            <div key={section.id} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                {Icon && <Icon className={`text-${section.color || 'blue'}-500`} size={18} />}
                <h3 className="text-base font-semibold text-gray-800">{section.label}</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {sectionContents.map((content) => renderContentByType(content))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {showFooter && (renderFooter ? renderFooter(data) : (
        <div className="text-center py-4">
          <p className="text-xs text-gray-400">{lastActiveLabel}: {formatDate((data as any).last_active_at)}</p>
        </div>
      ))}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReportModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Report</h2>
              <button onClick={() => setShowReportModal(false)} className="text-gray-500 hover:text-gray-700">
                <FaTimes size={20} />
              </button>
            </div>
            <p className="text-gray-600 text-sm mb-4">Please select a reason:</p>

            <div className="space-y-2 mb-4">
              {reportReasons.map(reason => (
                <label key={reason} className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="reportReason"
                    value={reason}
                    checked={reportReason === reason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="w-4 h-4 text-red-500"
                  />
                  <span className="text-gray-700">{reason}</span>
                </label>
              ))}
            </div>

            <textarea
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Additional details (optional)"
              className="w-full p-3 border rounded-xl mb-4 text-sm"
              rows={3}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 px-4 py-2.5 border rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReportSubmit}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyTabbedDetails;